import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IZone,
  IGroupsTree, IChildren,
} from '../../../store/modules/facility/i-facility';

import { TENANT_PATH } from '../../routing/paths.constants';
import { useActions } from '../../providers/actions-provider/actions.context';
import { useStore } from '../../../store';
import findGroupInTheTree from './findGroupInTheThee.utils';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';
import { USER_ID } from '../../../store/modules/user/user.constants';
import createBreadcrumbsFromRoute from '../../../utils/create-breadcrumbs';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useTenantData = () => {
  const { facilityActions, userActions } = useActions();
  const {
    groupsTree,
    groupsTree: { data },
    zones,
    zonesNew,
    groupTypes,
    currentUser,
    groups,
    zonesSaveState,
  } = useStore();
  const { location } = useHistory();
  const currentGroupID = location.pathname.split('/').pop() || TENANT_PATH;
  const localStorageService = useLocalStorage();
  const isAFirstPage = currentGroupID && `/${currentGroupID}` === TENANT_PATH;
  let currentGroupChildren : IGroupsTree | IChildren | null = findGroupInTheTree(data,
    +currentGroupID);
  const [copyGroups, setCopyGroups] = useState<IChildren | IGroupsTree>(!isAFirstPage
     && currentGroupChildren
    ? currentGroupChildren
    : data);
  const [copyZones, setCopyZones] = useState<IZone[]>([]);

  const zonesFilter = [
    { name: 'Name', value: 'name' },
    ...zones.data.propertyNames.map((name) => ({ name, value: name.toLowerCase() })),
  ];

  const currentGroup = findGroupInTheTree(data,
    +currentGroupID);
  const groupTitle = data && (currentGroupChildren ? currentGroupChildren?.name : 'Facilities');
  const groupTypesChierarchy = Object.values(groupTypes.data);

  const routes = createBreadcrumbsFromRoute(location.pathname, groups.data, currentUser.data.tenant || '');

  if (currentGroupChildren?.children) {
    currentGroupChildren = currentGroupChildren.children;
  }

  useEffect(() => {
    if (!currentUser.data.tenant) {
      const userID: number | null = localStorageService.get(USER_ID);
      if (userID) {
        userActions.getCurrentUser(userID);
      }
    }
  }, []);

  useEffect(() => {
    facilityActions.getGroups();
  }, []);

  useEffect(() => {
    facilityActions.getGroupsTree();
    facilityActions.getGroupTypes();
  }, []);

  useEffect(() => {
    if (!isAFirstPage && !currentGroup?.children) {
      facilityActions.getZones(currentGroupID);
    }
  }, [currentGroupChildren, currentGroup]);

  useEffect(() => {
    if (currentGroupChildren) {
      setCopyGroups(currentGroupChildren);
      return;
    }

    setCopyGroups(data);
  }, [currentGroupChildren, data]);

  useEffect(() => {
    if (!isAFirstPage) {
      setCopyZones(zonesNew.data);
    }
  }, [zonesNew]);

  return useMemo(() => ({
    ...groupsTree,
    isAFirstPage,
    groupTitle,
    groupType: currentGroup
      ? groupTypesChierarchy.find((group) => group.groupType_id === currentGroup.groupType_id)
      : groupTypesChierarchy.find((group) => group.groupType_id === null),
    currentGroup,
    zones: {
      ...zones,
      showZones: !isAFirstPage,
      data: { ...zones.data, data: copyZones },
    },
    zonesNew: {
      showZones: !isAFirstPage,
      data: copyZones,
    },
    zonesSaveState,
    currentGroupID,
    getZones: facilityActions.getZones,
    currentGroupChildren,
    loading: zones.loading || groupsTree.loading,
    data: copyGroups,
    tenantName: currentUser.data.tenant || '',
    zonesFilter,
    routes,
  }), [groupsTree, zones, groupTitle, location, copyGroups, copyZones, isAFirstPage, routes]);
};

export default useTenantData;
